import { useEffect, useState } from "react";

interface SpeechSynthesisVoice {
    lang: string;
    name: string;
}

interface UseSpeechSynthesisProps {
    onEnd?: () => void;
}

interface UseSpeechSynthesisState {
    voices: SpeechSynthesisVoice[];
    speak(args: any): void;
    cancel(): void;
    speaking: boolean;
    supported: boolean;
}

const useSpeechSynthesis = (props: UseSpeechSynthesisProps = {}): UseSpeechSynthesisState => {
    const { onEnd = () => { } } = props;
    const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
    const [speaking, setSpeaking] = useState(false);
    const [supported, setSupported] = useState(false);

    const processVoices = (voiceOptions: SpeechSynthesisVoice[]) => {
        setVoices(voiceOptions);
    };

    const getVoices = () => {
        // Firefox seems to have voices upfront and never calls the
        // voiceschanged event
        let voiceOptions = window.speechSynthesis.getVoices();
        if (voiceOptions.length > 0) {
            processVoices(voiceOptions);
            return;
        }
        window.speechSynthesis.onvoiceschanged = (event: any) => {
            voiceOptions = event.target.getVoices();
            processVoices(voiceOptions);
        };
    };

    const handleEnd = () => {
        setSpeaking(false);
        onEnd();
    };

    useEffect(() => {
        if (typeof window !== "undefined" && window.speechSynthesis) {
            setSupported(true);
            getVoices();
        }
    }, []);

    const speak = (args: {
        voice?: SpeechSynthesisVoice | null;
        text: string;
        rate?: number;
        pitch?: number;
        volume?: number;
    }) => {
        const { voice = null, text = "", rate = 1, pitch = 1, volume = 1 } = args;
        if (!supported) return;
        setSpeaking(true);
        // Firefox won't repeat an utterance that has been
        // spoken, so we need to create a new instance each time
        const utterance: any = new SpeechSynthesisUtterance();
        utterance.text = text;
        utterance.voice = voice;
        utterance.onend = handleEnd;
        utterance.rate = rate;
        utterance.pitch = pitch;
        utterance.volume = volume;
        window.speechSynthesis.speak(utterance);
    };

    const cancel = () => {
        if (!supported) return;
        setSpeaking(false);
        window.speechSynthesis.cancel();
    };

    return {
        supported,
        speak,
        speaking,
        cancel,
        voices,
    };
};

export default useSpeechSynthesis;
